"use client"

import { Plus } from "@phosphor-icons/react"
import { navigation } from "@src/constants/routes"
import { useIsActiveLink } from "@src/hooks/useIsActiveLink"
import { cn } from "@src/utils/cn"
import Link from "next/link"
import { useEffect, useRef, useState, useCallback } from "react"
import styles from "./NavbarDesktop.module.css"
import buttonStyles from "@src/components/Wallet/VeaxButton.module.css"

export function NavbarDesktop() {
  const { isActive } = useIsActiveLink()
  const navRef = useRef<HTMLElement>(null)
  const highlightRef = useRef<HTMLSpanElement>(null)
  const linksRef = useRef<Map<string, HTMLAnchorElement>>(new Map())
  const [mounted, setMounted] = useState(false)

  const isAccountActive = isActive(navigation.account)
  const isTradeActive = isActive(navigation.home) || isActive(navigation.otc)
  const isDepositActive = isActive(navigation.deposit)

  const updateHighlight = useCallback(() => {
    if (!highlightRef.current || !navRef.current) return

    const activeLink = Array.from(linksRef.current.values()).find(
      (link) => link.dataset.active === "true"
    )

    if (activeLink) {
      const navRect = navRef.current.getBoundingClientRect()
      const linkRect = activeLink.getBoundingClientRect()

      const left = linkRect.left - navRect.left + linkRect.width / 2 - linkRect.width / 2
      const width = linkRect.width

      highlightRef.current.style.visibility = "visible"
      highlightRef.current.style.width = `${width}px`
      highlightRef.current.style.left = `${left}px`
    } else {
      highlightRef.current.style.visibility = "hidden"
    }
  }, [])

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!mounted) return

    const timer = setTimeout(() => {
      updateHighlight()
    }, 100)

    window.addEventListener("resize", updateHighlight)
    return () => {
      clearTimeout(timer)
      window.removeEventListener("resize", updateHighlight)
    }
  }, [mounted, isAccountActive, isTradeActive, isDepositActive, updateHighlight])

  const setLinkRef = useCallback((key: string, element: HTMLAnchorElement | null) => {
    if (element) {
      linksRef.current.set(key, element)
    } else {
      linksRef.current.delete(key)
    }
  }, [])

  return (
    <nav ref={navRef} className={styles.nav}>
      <Link
        href={navigation.account}
        ref={(el) => setLinkRef("account", el)}
        data-active={isAccountActive}
        className={cn(styles.navItem, isAccountActive && styles.active)}
      >
        ACCOUNT
      </Link>

      <Link
        href={navigation.home}
        ref={(el) => setLinkRef("trade", el)}
        data-active={isTradeActive}
        className={cn(styles.navItem, isTradeActive && styles.active)}
      >
        SWAP
      </Link>

      <Link
        href={navigation.deposit}
        ref={(el) => setLinkRef("deposit", el)}
        data-active={isDepositActive}
        className={cn(styles.navItem, isDepositActive && styles.active)}
      >
        DEPOSIT
      </Link>

      <span ref={highlightRef} className={styles.highlight} style={{ visibility: "hidden" }} />
    </nav>
  )
}

export function NavbarDeposit() {
  return (
    <Link href={navigation.deposit}>
      <button type="button" className={cn(buttonStyles.veaxButton, buttonStyles.secondary)}>
        <span className={buttonStyles.content}>
          <Plus className="size-3" weight="bold" />
          DEPOSIT
        </span>
      </button>
    </Link>
  )
}
